(() => {
  if (window.__awVoiceArticlesLoaded) {
    return;
  }

  window.__awVoiceArticlesLoaded = true;

  const instances = new Map();
  const reducedMotionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
  const selectors = {
    root: "[data-aw-voice-articles]",
    slider: "[data-aw-voice-slider]",
    slide: "[data-aw-voice-slide]",
    prev: "[data-aw-voice-prev]",
    next: "[data-aw-voice-next]",
  };

  class AWVoiceArticles {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId || "";
      this.slider = root.querySelector(selectors.slider);
      this.slides = Array.from(root.querySelectorAll(selectors.slide));
      this.prevButton = root.querySelector(selectors.prev);
      this.nextButton = root.querySelector(selectors.next);
      this.activeIndex = Math.max(
        0,
        this.slides.findIndex((slide) => slide.classList.contains("is-active"))
      );
      this.autoplayEnabled = root.dataset.autoplay === "true";
      this.autoplaySeconds = Number.parseInt(root.dataset.autoplaySeconds || "5", 10);
      this.autoplayTimer = null;
      this.isPaused = false;

      this.onPrevClick = this.handlePrevClick.bind(this);
      this.onNextClick = this.handleNextClick.bind(this);
      this.onMouseEnter = this.handleMouseEnter.bind(this);
      this.onMouseLeave = this.handleMouseLeave.bind(this);
      this.onFocusIn = this.handleFocusIn.bind(this);
      this.onFocusOut = this.handleFocusOut.bind(this);

      this.bindEvents();
      this.goTo(this.activeIndex, { shouldFocus: false });
      this.startAutoplay();
    }

    updateButtonState(button, isDisabled) {
      if (!button) {
        return;
      }

      button.disabled = false;
      button.classList.toggle("is-disabled", isDisabled);
      button.setAttribute("aria-disabled", isDisabled ? "true" : "false");
    }

    bindEvents() {
      if (this.prevButton) {
        this.prevButton.addEventListener("click", this.onPrevClick);
      }

      if (this.nextButton) {
        this.nextButton.addEventListener("click", this.onNextClick);
      }

      if (this.slider) {
        this.slider.addEventListener("mouseenter", this.onMouseEnter);
        this.slider.addEventListener("mouseleave", this.onMouseLeave);
        this.slider.addEventListener("focusin", this.onFocusIn);
        this.slider.addEventListener("focusout", this.onFocusOut);
      }
    }

    handlePrevClick() {
      if (this.prevButton && this.prevButton.getAttribute("aria-disabled") === "true") {
        return;
      }

      if (this.slides.length <= 1) {
        return;
      }

      const step = this.getButtonStep();
      const nextIndex = (this.activeIndex - step + this.slides.length) % this.slides.length;
      this.goTo(nextIndex, { shouldFocus: false });
      this.restartAutoplay();
    }

    handleNextClick() {
      if (this.nextButton && this.nextButton.getAttribute("aria-disabled") === "true") {
        return;
      }

      if (this.slides.length <= 1) {
        return;
      }

      const step = this.getButtonStep();
      const nextIndex = (this.activeIndex + step) % this.slides.length;
      this.goTo(nextIndex, { shouldFocus: false });
      this.restartAutoplay();
    }

    getButtonStep() {
      if (this.slides.length <= 2) {
        return 1;
      }

      return 2;
    }

    handleMouseEnter() {
      this.isPaused = true;
      this.stopAutoplay();
    }

    handleMouseLeave() {
      this.isPaused = false;
      this.startAutoplay();
    }

    handleFocusIn() {
      this.isPaused = true;
      this.stopAutoplay();
    }

    handleFocusOut(event) {
      if (this.slider && event.relatedTarget && this.slider.contains(event.relatedTarget)) {
        return;
      }

      this.isPaused = false;
      this.startAutoplay();
    }

    getFocusableElements(slide) {
      return Array.from(slide.querySelectorAll("a[href], button, [tabindex]"));
    }

    goTo(index, options = {}) {
      const { shouldFocus = false } = options;

      if (!this.slides.length) {
        return;
      }

      const normalizedIndex = Math.min(this.slides.length - 1, Math.max(0, index));
      this.activeIndex = normalizedIndex;

      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === normalizedIndex;

        slide.classList.toggle("is-active", isActive);
        slide.setAttribute("aria-hidden", isActive ? "false" : "true");

        this.getFocusableElements(slide).forEach((element) => {
          if (!isActive) {
            element.setAttribute("tabindex", "-1");
          } else if (element.getAttribute("tabindex") === "-1") {
            element.removeAttribute("tabindex");
          }
        });
      });

      this.updateButtons();

      if (shouldFocus) {
        const activeLink = this.slides[normalizedIndex].querySelector("a[href]");

        if (activeLink) {
          activeLink.focus();
        }
      }
    }

    updateButtons() {
      const shouldDisable = this.slides.length <= 1;

      this.updateButtonState(this.prevButton, shouldDisable);
      this.updateButtonState(this.nextButton, shouldDisable);
    }

    canAutoplay() {
      return (
        this.autoplayEnabled &&
        this.slides.length > 1 &&
        !reducedMotionQuery.matches &&
        !this.isPaused
      );
    }

    startAutoplay() {
      this.stopAutoplay();

      if (!this.canAutoplay()) {
        return;
      }

      const interval = Math.max(3, this.autoplaySeconds || 5) * 1000;

      this.autoplayTimer = window.setInterval(() => {
        const nextIndex = (this.activeIndex + 1) % this.slides.length;
        this.goTo(nextIndex, { shouldFocus: false });
      }, interval);
    }

    stopAutoplay() {
      if (!this.autoplayTimer) {
        return;
      }

      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    restartAutoplay() {
      this.startAutoplay();
    }

    selectBlock(blockId) {
      if (!blockId) {
        return;
      }

      const slideIndex = this.slides.findIndex((slide) => slide.dataset.blockId === blockId);

      if (slideIndex < 0) {
        return;
      }

      this.stopAutoplay();
      this.goTo(slideIndex, { shouldFocus: false });
    }

    deselectBlock() {
      this.startAutoplay();
    }

    destroy() {
      this.stopAutoplay();

      if (this.prevButton) {
        this.prevButton.removeEventListener("click", this.onPrevClick);
      }

      if (this.nextButton) {
        this.nextButton.removeEventListener("click", this.onNextClick);
      }

      if (this.slider) {
        this.slider.removeEventListener("mouseenter", this.onMouseEnter);
        this.slider.removeEventListener("mouseleave", this.onMouseLeave);
        this.slider.removeEventListener("focusin", this.onFocusIn);
        this.slider.removeEventListener("focusout", this.onFocusOut);
      }
    }
  }

  function getRoot(target) {
    if (!target) {
      return null;
    }

    if (typeof target.matches === "function" && target.matches(selectors.root)) {
      return target;
    }

    if (typeof target.querySelector === "function") {
      const root = target.querySelector(selectors.root);

      if (root) {
        return root;
      }
    }

    if (typeof target.closest === "function") {
      return target.closest(selectors.root);
    }

    return null;
  }

  function destroySection(sectionId) {
    const instance = instances.get(sectionId);

    if (!instance) {
      return;
    }

    instance.destroy();
    instances.delete(sectionId);
  }

  function initSection(root) {
    if (!root) {
      return;
    }

    const sectionId = root.dataset.sectionId;

    if (!sectionId) {
      return;
    }

    destroySection(sectionId);
    instances.set(sectionId, new AWVoiceArticles(root));
  }

  function initAll(container = document) {
    container.querySelectorAll(selectors.root).forEach((root) => {
      initSection(root);
    });
  }

  document.addEventListener("shopify:section:load", (event) => {
    initSection(getRoot(event.target));
  });

  document.addEventListener("shopify:section:unload", (event) => {
    const root = getRoot(event.target);

    if (!root) {
      return;
    }

    destroySection(root.dataset.sectionId);
  });

  document.addEventListener("shopify:block:select", (event) => {
    const sectionId = event.detail && event.detail.sectionId;
    const blockId = event.detail && event.detail.blockId;
    const instance = sectionId ? instances.get(sectionId) : null;

    if (!instance) {
      return;
    }

    instance.selectBlock(blockId);
  });

  document.addEventListener("shopify:block:deselect", (event) => {
    const sectionId = event.detail && event.detail.sectionId;
    const instance = sectionId ? instances.get(sectionId) : null;

    if (!instance) {
      return;
    }

    instance.deselectBlock();
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => {
      initAll(document);
    });
  } else {
    initAll(document);
  }
})();
